import { c as _c } from "react/compiler-runtime";
import type { ResourceContext } from '@onefootprint/request-types/percy';
import { createContext, type ReactNode, useContext } from 'react';

// Maps a resource id → the friendly name captured for it in the user message's
// `mentions` snapshot. `IdBadge` reads this to render an inline pill with the
// name at rest instead of a raw fp_id — since the name is snapshotted at send
// time, it stays stable across renames and viewer permission changes.
const MessageMentionsContext = createContext<ReadonlyMap<string, string>>(new Map());
type MessageMentionsProviderProps = {
  mentions: ResourceContext[];
  children: ReactNode;
};
const idOf = (r: ResourceContext): string => 'id' in r ? r.id : r.key;
export const MessageMentionsProvider = t0 => {
  const $ = _c(5);
  const {
    mentions,
    children
  } = t0;
  let value;
  if ($[0] !== mentions) {
    value = new Map();
    for (const r of mentions) {
      const name = "name" in r ? r.name : null;
      if (name) {
        value.set(idOf(r), name);
      }
    }
    $[0] = mentions;
    $[1] = value;
  } else {
    value = $[1];
  }
  let t1;
  if ($[2] !== children || $[3] !== value) {
    t1 = <MessageMentionsContext.Provider value={value}>{children}</MessageMentionsContext.Provider>;
    $[2] = children;
    $[3] = value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

// Returns the snapshotted name for the id if this markdown is being rendered
// inside a user message whose `mentions` snapshot carried the id. Undefined
// outside a provider (e.g. Percy assistant messages), which is the signal to
// fall back to whatever the caller shows for unresolved ids.
export const useMessageMentionName = id => {
  const $ = _c(3);
  const t0 = useContext(MessageMentionsContext);
  let t1;
  if ($[0] !== id || $[1] !== t0) {
    t1 = t0.get(id);
    $[0] = id;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};